import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Loader2, Users, Trash2, KeyRound, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CLIENT_TYPE_LABELS, formatCurrency, formatDate, STAGE_STATUS_LABELS, STAGE_STATUS_STYLES } from '../components/client-portal/clientPortalApi';
import AdminClientCreateDialog from '../components/admin/AdminClientCreateDialog';
import AdminClientDetailDrawer from '../components/admin/AdminClientDetailDrawer';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function ClientManagement() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [createdMessage, setCreatedMessage] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };

  const reload = async () => {
    const res = await fetch(`${backendUrl}/api/admin/clients`, { headers: auth });
    if (res.ok) setClients(await res.json());
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
    // eslint-disable-next-line
  }, []);

  const handleCreated = async (result) => {
    setShowCreate(false);
    setCreatedMessage({
      email: result.email,
      password: result.temporary_password,
      name: result.name,
    });
    await reload();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}" and all their documents? This cannot be undone.`)) return;
    const res = await fetch(`${backendUrl}/api/admin/clients/${c.id}`, { method: 'DELETE', headers: auth });
    if (res.ok) reload();
  };

  const handleResetPassword = async (c) => {
    const res = await fetch(`${backendUrl}/api/admin/clients/${c.id}/reset-password`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (res.ok) {
      const data = await res.json();
      setCreatedMessage({ email: c.email, password: data.new_password, name: c.name, isReset: true });
    }
  };

  const filtered = clients.filter((c) =>
    !search.trim() ||
    [c.name, c.email, c.broker_name].filter(Boolean).some((s) => s.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-semibold text-slate-900">Clients</h1>
          <span className="ml-auto text-sm text-slate-500">{clients.length} total</span>
        </div>

        {createdMessage && (
          <div className="mb-4 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-4 text-sm text-emerald-900" data-testid="temp-pw-banner">
            <div className="font-semibold">{createdMessage.isReset ? 'Password reset' : 'Client created'} — please share these details with {createdMessage.name}:</div>
            <div className="mt-1.5 font-mono text-xs bg-white rounded px-3 py-2 inline-block">
              <div>Email: <span className="text-slate-900">{createdMessage.email}</span></div>
              <div>Temporary password: <span className="text-emerald-700 font-bold select-all">{createdMessage.password}</span></div>
              <div className="text-slate-500 mt-1">Portal URL: <span className="text-slate-700">/client-portal</span></div>
            </div>
            <button
              type="button"
              onClick={() => setCreatedMessage(null)}
              className="block mt-2 text-emerald-700 hover:underline text-xs"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email or broker"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="client-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              data-testid="new-client-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New Client
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto text-slate-300" />
              <div className="mt-2 text-sm">No clients match your search.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left font-medium px-4 py-2.5">Client</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">Type</th>
                  <th className="text-left font-medium px-4 py-2.5">Stage</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">Loan</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">Created</th>
                  <th className="text-right font-medium px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const stage = c.current_stage || {};
                  const style = STAGE_STATUS_STYLES[stage.status] || STAGE_STATUS_STYLES.not_started;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`client-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-slate-500 text-xs">{c.email}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-700">
                        {CLIENT_TYPE_LABELS[c.client_type] || c.client_type}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{stage.stage_label || '—'}</div>
                        <div className="text-xs">
                          <span className={`px-2 py-0.5 rounded-full ring-1 ${style}`}>{STAGE_STATUS_LABELS[stage.status] || '—'}</span>
                          <span className="ml-2 text-slate-500">{c.progress_percent || 0}%</span>
                          {c.outstanding_documents > 0 && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px]">
                              {c.outstanding_documents} doc{c.outstanding_documents > 1 ? 's' : ''} pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="text-slate-700">{c.loan_amount ? formatCurrency(c.loan_amount) : '—'}</div>
                        <div className="text-xs text-slate-500">{c.loan_purpose || ''}</div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setSelectedId(c.id)} title="View" data-testid={`view-client-${c.id}`}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleResetPassword(c)} title="Reset password" data-testid={`reset-pw-${c.id}`}
                            className="p-1.5 rounded-md hover:bg-amber-50 text-amber-600">
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(c)} title="Delete" data-testid={`delete-client-${c.id}`}
                            className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && (
        <AdminClientCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          token={token}
        />
      )}

      {selectedId && (
        <AdminClientDetailDrawer
          clientId={selectedId}
          token={token}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}
